import React, { useState, useMemo } from 'react';
import ToolShell, { ACCENT, INK, fmt, Field, input, card, btn, printDoc } from '../tools/ToolShell';

/*
 * PUBLIC, no-login lead-magnet tool: /tools/influencer-invoice-generator
 * Targets "influencer invoice format india". Creator fills their details + the
 * brand deal -> live invoice preview with GST + TDS auto-computed -> download /
 * print as PDF. The strongest funnel: invoicing is CreatorKhata's core feature.
 */

const num = (s) => parseFloat(String(s).replace(/[^0-9.]/g, '')) || 0;

export default function InvoiceToolPage() {
  const [from, setFrom] = useState('');
  const [pan, setPan] = useState('');
  const [to, setTo] = useState('');
  const [desc, setDesc] = useState('');
  const [amount, setAmount] = useState('');
  const [gstOn, setGstOn] = useState(false);
  const [tds, setTds] = useState('10');

  const calc = useMemo(() => {
    const base = num(amount);
    if (!base) return null;
    const gst = gstOn ? Math.round(base * 0.18) : 0;
    const total = base + gst;
    const tdsAmt = Math.round(base * (num(tds) / 100));  // TDS on base (pre-GST)
    const net = total - tdsAmt;
    return { base, gst, total, tdsAmt, net };
  }, [amount, gstOn, tds]);

  const invoiceHtml = () => {
    const today = new Date().toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
    const inv = 'INV-' + new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const line = (l, v, bold) => `<tr><td style="padding:8px 0;color:#555">${l}</td><td style="padding:8px 0;text-align:right;${bold ? 'font-weight:700;color:' + ACCENT : ''}">${v}</td></tr>`;
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Invoice — ${from || 'Creator'}</title>
      <style>body{font-family:Arial,Helvetica,sans-serif;color:${INK};max-width:640px;margin:24px auto;padding:0 18px}
      h1{font-size:22px;margin:0}.muted{color:#888;font-size:13px}.box{border:1px solid #eee;border-radius:10px;padding:14px 16px;margin-top:14px}
      table{width:100%;border-collapse:collapse}.tot{border-top:2px solid ${ACCENT};margin-top:6px}
      .foot{margin-top:22px;font-size:11px;color:#aaa;text-align:center}@media print{.noprint{display:none}}</style></head>
      <body>
      <div style="display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid ${ACCENT};padding-bottom:12px">
        <div><h1>${from || 'Your Name'}</h1>${pan ? `<div class="muted">PAN: ${pan}</div>` : ''}</div>
        <div style="text-align:right"><div style="font-weight:700">INVOICE</div><div class="muted">${inv}<br>${today}</div></div>
      </div>
      <div class="box"><div class="muted">BILL TO</div><div style="font-weight:700;margin-top:2px">${to || 'Brand name'}</div></div>
      <div class="box"><table>
        <tr><td style="padding:8px 0"><b>${desc || 'Sponsored content deliverable'}</b></td><td style="padding:8px 0;text-align:right">${fmt(calc?.base || 0)}</td></tr>
      </table></div>
      <table style="margin-top:14px">
        ${line('Subtotal', fmt(calc?.base || 0))}
        ${gstOn ? line('GST (18%)', fmt(calc?.gst || 0)) : ''}
        ${line('<b>Total</b>', `<b>${fmt(calc?.total || 0)}</b>`)}
        ${num(tds) ? line(`Less: TDS (${tds}%)`, '−' + fmt(calc?.tdsAmt || 0)) : ''}
      </table>
      <table class="tot">${line('Net payable', fmt(calc?.net || 0), true)}</table>
      <p class="muted" style="margin-top:14px">Payment due within 30 days. ${gstOn ? '' : 'GST not applicable (below registration threshold). '}TDS, where shown, is deducted by the payer and deposited with the government against the creator's PAN.</p>
      <div class="foot">Generated free with CreatorKhata — creatorkhata.com</div>
      <button class="noprint" onclick="window.print()" style="margin-top:16px;background:${ACCENT};color:#fff;border:none;border-radius:8px;padding:10px 18px;font-weight:700;cursor:pointer">Print / Save as PDF</button>
      </body></html>`;
  };

  return (
    <ToolShell
      seo={{
        title: 'Free Influencer Invoice Generator (India, GST + TDS) — CreatorKhata',
        description: 'Free invoice generator for Indian YouTubers & influencers. Auto-calculates 18% GST and TDS, and downloads a professional brand-deal invoice as PDF. No signup.',
        path: '/tools/influencer-invoice-generator',
      }}
      h1="Influencer Invoice Generator (India)"
      sub="Create a professional brand-deal invoice with GST and TDS handled correctly — then download it as a PDF to send the brand. No signup."
      cta={{
        heading: 'One invoice is easy. Fifty is a spreadsheet nightmare.',
        body: 'CreatorKhata stores your invoices, tracks which brands have paid, totals your TDS for ITR, and chases overdue payments — so you actually get paid on time.',
      }}
    >
      {/* Inputs */}
      <div style={card()}>
        <Field label="Your name / channel"><input style={input} value={from} onChange={e => setFrom(e.target.value)} placeholder="e.g. Arjun Finance" /></Field>
        <Field label="Your PAN (optional)"><input style={input} value={pan} onChange={e => setPan(e.target.value)} placeholder="ABCDE1234F" /></Field>
        <Field label="Brand / client name"><input style={input} value={to} onChange={e => setTo(e.target.value)} placeholder="e.g. Acme India Pvt Ltd" /></Field>
        <Field label="Deliverable description"><input style={input} value={desc} onChange={e => setDesc(e.target.value)} placeholder="1 dedicated YouTube video + 1 Short" /></Field>
        <Field label="Amount (₹, before tax)"><input style={input} type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="e.g. 80000" /></Field>
        <Field label="Add 18% GST?">
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {[['Yes', true], ['No', false]].map(([label, val]) => (
              <button key={label} onClick={() => setGstOn(val)} style={{
                flex: 1, padding: '0.55rem', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem',
                border: `1.5px solid ${gstOn === val ? ACCENT : '#e0e0e0'}`,
                background: gstOn === val ? ACCENT : 'white', color: gstOn === val ? 'white' : '#666',
              }}>{label}</button>
            ))}
          </div>
        </Field>
        <Field label="TDS deducted by brand (%)"><input style={input} type="number" value={tds} onChange={e => setTds(e.target.value)} placeholder="10" /></Field>
      </div>

      {/* Preview — updates live as you type, no submit button needed */}
      <div style={card()}>
        {!calc ? (
          <div style={{ textAlign: 'center', color: '#aaa', padding: '2.5rem 1rem' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🧾</div>
            <div style={{ marginBottom: '1.25rem' }}>
              Your invoice builds here <b>automatically</b> as you fill the form — no submit button.<br />
              Just enter an <b style={{ color: ACCENT }}>amount</b> to start.
            </div>
            <button disabled style={{ ...btn(ACCENT), width: '100%', opacity: 0.4, cursor: 'not-allowed' }}>⬇️ Download / Print Invoice</button>
          </div>
        ) : (
          <div>
            <div style={{ borderBottom: `3px solid ${ACCENT}`, paddingBottom: '0.6rem', marginBottom: '0.6rem' }}>
              <div style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.2rem' }}>{from || 'Your Name'}</div>
              <div style={{ color: '#888', fontSize: '0.8rem' }}>Invoice for {to || 'brand'}</div>
            </div>
            <Row label={desc || 'Deliverable'} value={fmt(calc.base)} />
            {gstOn && <Row label="GST (18%)" value={fmt(calc.gst)} />}
            <Row label="Total" value={fmt(calc.total)} />
            {num(tds) > 0 && <Row label={`Less TDS (${tds}%)`} value={'−' + fmt(calc.tdsAmt)} />}
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.7rem 0 0.2rem', borderTop: `2px solid ${ACCENT}`, marginTop: '0.4rem' }}>
              <b>Net payable</b><b style={{ color: ACCENT }}>{fmt(calc.net)}</b>
            </div>
            <button onClick={() => printDoc(invoiceHtml())} style={{ ...btn(ACCENT), width: '100%', marginTop: '0.9rem' }}>⬇️ Download / Print Invoice</button>
          </div>
        )}
      </div>
    </ToolShell>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #eee' }}>
      <span style={{ fontSize: '0.85rem', color: '#555' }}>{label}</span>
      <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{value}</span>
    </div>
  );
}
